import React, { MouseEventHandler, ReactElement, ReactNode, DependencyList } from 'react';
import theme from './EntityIcon.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-light-svg-icons';
import { classnames } from '@/utils/html';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Optional } from '@/types';
import { useCacheBuster } from '@/utils/hooks';
import { Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';

export interface EntityIconProps {
    url: string | null;
    sizeInPx?: number;
    className?: string;
    emptyIcon?: IconProp;
    onClick?: MouseEventHandler<HTMLDivElement>;
    customFallback?: ReactElement | null;
    transparent?: boolean;
    shape: 'circle' | 'square';
    fit: 'contain' | 'cover';
    cacheDependencies?: DependencyList;
}

export type ExternalEntityIconProps = Optional<Omit<EntityIconProps, 'url'>, 'shape' | 'fit'>;

const EntityIcon = React.forwardRef<HTMLDivElement, EntityIconProps>(
    (
        {
            url,
            sizeInPx = 50,
            className,
            transparent = false,
            emptyIcon = faUser as any,
            onClick,
            customFallback = <FontAwesomeIcon icon={emptyIcon} className={theme.icon} fixedWidth />,
            shape,
            fit,
            cacheDependencies,
        },
        ref
    ) => {
        let content: ReactNode = customFallback;

        const cacheBustedUrl = useCacheBuster(url || '', cacheDependencies || []);

        if (url) {
            const actualUrl = cacheDependencies ? cacheBustedUrl : url;
            content = <Thumbnail alt="Thumbnail" src={actualUrl} lazy={false} fallback={customFallback} />;
        }

        return (
            <div
                ref={ref}
                onClick={onClick}
                style={{
                    width: sizeInPx,
                    height: sizeInPx,
                    fontSize: sizeInPx,
                }}
                className={classnames(
                    theme.wrapper,
                    theme[shape],
                    theme[fit],
                    { [theme.transparent]: transparent },
                    className
                )}>
                {content}
            </div>
        );
    }
);

export default EntityIcon;
